import type { GridDataSource, GridGetRowsParams, GridGetRowsResponse, GridSortModel } from "@mui/x-data-grid-premium";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import { deduplicatePendingRequests } from "@entrilia/shared/utilities/promiseHelper.ts";
import type {
  DrillDownRow,
  FailedMeasure,
  ListDataSettings,
  ReportField,
} from "../../../../../../../../api/types/svTypes.ts";
import type {
  FetchRowsPayload,
  GridDataSourceWithRangePagingResponse,
} from "@entrilia/shared/components/reporting/text-filter-grid/useGridDataSourceWithRangePaging.ts";

import { getListAggregatedValue, LIST_PAGE_SIZE } from "./listGridHelper.ts";

export interface ListPageConfiguration<P extends object> {
  payload: P;
  fields: ReportField[];
  settings: ListDataSettings;
}

export interface ListReportFailures {
  resultId: string;
  failedMeasures: FailedMeasure[];
}

export interface ListPageResponse extends GridDataSourceWithRangePagingResponse<DrillDownRow> {
  failedMeasures: FailedMeasure[];
}

export type ListPageFetcher<P extends object> = (params: FetchRowsPayload<P>) => Promise<ApiResponse<ListPageResponse>>;

export interface PreparedListReport<P extends object> extends ListReportFailures {
  configuration: ListPageConfiguration<P>;
  sortModel: GridSortModel;
  totalRows: number | undefined;
  dataSource: GridDataSource;
  activate: () => void;
  dispose: () => void;
}

export const prepareListReport = async <P extends object>(
  configuration: ListPageConfiguration<P>,
  sortModel: GridSortModel,
  fetchRows: ListPageFetcher<P>,
  signal: AbortSignal,
  onFailuresChanged?: (failures: ListReportFailures) => void
): Promise<ApiResponse<PreparedListReport<P>>> => {
  const firstPage = await fetchRows({
    ...configuration.payload,
    start: 0,
    end: LIST_PAGE_SIZE - 1,
    filterModel: { items: [] },
    sortModel,
    range: { skip: 0, take: LIST_PAGE_SIZE, totals: true },
    signal,
  });
  signal.throwIfAborted();
  if (!firstPage.success) {
    return { success: false, error: firstPage.error };
  }

  // Canceling a replacement must not abort paging on the result still displayed.
  let controller = new AbortController();
  const { items, total, grandTotalItem } = firstPage.data;
  const resultId = generateGuid();
  let failedMeasures = firstPage.data.failedMeasures;
  const mergeFailures = (pageFailures: FailedMeasure[]) => {
    const failures = [
      ...new Map([...failedMeasures, ...pageFailures].map((failure) => [failure.fieldId, failure])).values(),
    ];
    if (failures.length === failedMeasures.length) {
      return;
    }
    failedMeasures = failures;
    onFailuresChanged?.({ resultId, failedMeasures });
  };
  const loadRows = async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
    const pageSignal = controller.signal;
    pageSignal.throwIfAborted();
    const skip = Number(params.start ?? 0);
    const take = params.end === undefined ? LIST_PAGE_SIZE : params.end - skip + 1;
    if (!Number.isSafeInteger(skip) || skip < 0 || !Number.isSafeInteger(take) || take < 1) {
      throw new Error("Invalid report page range.");
    }
    if (skip + take <= items.length || items.length === total) {
      return { rows: items.slice(skip, skip + take), rowCount: total, aggregateRow: grandTotalItem };
    }

    const response = await fetchRows({
      ...params,
      ...configuration.payload,
      sortModel,
      range: { skip, take, totals: false },
      signal: pageSignal,
    });
    pageSignal.throwIfAborted();
    if (!response.success) {
      throw new Error(response.error?.message ?? "This report page could not be loaded.");
    }
    mergeFailures(response.data.failedMeasures);
    return { rows: response.data.items, rowCount: total, aggregateRow: grandTotalItem };
  };

  return {
    success: true,
    data: {
      resultId,
      failedMeasures,
      configuration,
      sortModel,
      totalRows: total,
      dataSource: {
        getRows: deduplicatePendingRequests(loadRows, (params) => `${params.start}:${params.end}`),
        getAggregatedValue: (row, field) => {
          const value = getListAggregatedValue(configuration.fields, row as DrillDownRow, field);
          return value == null ? "" : String(value);
        },
      },
      activate: () => {
        if (controller.signal.aborted) {
          controller = new AbortController();
        }
      },
      dispose: () => controller.abort(),
    },
  };
};
